import React, { useState } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Kicker, Area, Modal, Empty } from "@/components/kit";
import { Mark } from "@/components/Logo";
import { Pencil } from "lucide-react";

const Panel = ({ label, text, empty }) => (
  <Card className="p-6" data-testid={`charter-${label.toLowerCase()}`}>
    <Kicker>{label}</Kicker>
    {text ? (
      <p className="serif text-xl sm:text-2xl leading-snug mt-4 whitespace-pre-line">{text}</p>
    ) : (
      <p className="text-sm mt-4" style={{ color: "var(--ink-mute)" }}>{empty}</p>
    )}
  </Card>
);

export default function Charter() {
  const { settings, me, setSettings, activeMembers } = useTable();
  const charter = settings.charter || {};
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const start = () => {
    setForm({ vision: charter.vision || "", mission: charter.mission || "", values: charter.values || "" });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { charter: form });
      setSettings((s) => ({ ...s, charter: form }));
      setOpen(false);
      toast.success("Charter saved.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const values = (charter.values || "").split("\n").map((v) => v.trim()).filter(Boolean);

  return (
    <div data-testid="charter-page">
      <PageHead
        kicker="The Charter"
        title="Vision, mission and value system"
        sub="What the forum is for, how it behaves, and the standards the members hold each other to. Written once, revisited when the table decides."
        right={
          me.isAdmin ? (
            <button className="btn-quiet" data-testid="charter-edit" onClick={start}>
              <Pencil size={13} /> {charter.vision || charter.mission || values.length ? "Edit charter" : "Write the charter"}
            </button>
          ) : null
        }
      />

      <div className="flex items-center gap-3 mb-6">
        <Mark size={34} />
        <span className="text-[11px] tracking-[0.22em] uppercase" style={{ color: "var(--ink-mute)" }}>
          {activeMembers.length} seats · one table
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel label="Vision" text={charter.vision} empty="Not written yet — the vision the forum is walking towards." />
        <Panel label="Mission" text={charter.mission} empty="Not written yet — what the forum does, every chapter, to get there." />
      </div>

      <div className="mt-4">
        <Card className="p-6" data-testid="charter-values">
          <Kicker>Value system</Kicker>
          {values.length === 0 ? (
            <p className="text-sm mt-4" style={{ color: "var(--ink-mute)" }}>
              Not written yet — the standards the table keeps, one per line.
            </p>
          ) : (
            <ol className="mt-5 space-y-4">
              {values.map((v, i) => (
                <li key={i} className="flex gap-4 items-baseline">
                  <span className="numeral text-sm shrink-0" style={{ color: "var(--brass)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="serif text-lg leading-snug">{v}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {!charter.vision && !charter.mission && values.length === 0 && !me.isAdmin && (
        <div className="mt-4">
          <Empty line="The Moderator will write this with the table." testId="charter-empty" />
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="The charter" testId="charter-modal">
        <Area label="Vision" value={form.vision || ""} data-testid="charter-vision-input"
          onChange={(e) => setForm({ ...form, vision: e.target.value })} />
        <Area label="Mission" value={form.mission || ""} data-testid="charter-mission-input"
          onChange={(e) => setForm({ ...form, mission: e.target.value })} />
        <Area label="Value system (one per line)" value={form.values || ""} data-testid="charter-values-input"
          onChange={(e) => setForm({ ...form, values: e.target.value })} />
        <button className="btn-ink w-full" data-testid="charter-save" disabled={busy} onClick={save}>Save charter</button>
      </Modal>
    </div>
  );
}
